import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Repayment, PaginatedRepayments, DriverSummary } from "@/types/repayment";

export interface ListRepaymentParams {
  search?: string;
  driverId?: string;
  type?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: number;
}

export interface PayRepaymentData {
  paymentDate: string;
  method: string;
  notes?: string;
  accountId?: string;
}

const KEYS = {
  list: (params: ListRepaymentParams) => ["repayments", params] as const,
  driverSummary: (driverId: string) => ["repayments", "summary", driverId] as const,
};

export function useRepayments(params: ListRepaymentParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api.get<PaginatedRepayments>("/repayments", { params }).then((r) => r.data),
  });
}

export function useDriverSummary(driverId: string) {
  return useQuery({
    queryKey: KEYS.driverSummary(driverId),
    queryFn: () =>
      api
        .get<DriverSummary>(`/repayments/summary/driver/${driverId}`)
        .then((r) => r.data),
    enabled: !!driverId,
  });
}

export function usePayRepayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PayRepaymentData }) =>
      api.post<Repayment>(`/repayments/${id}/pay`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repayments"] });
      // O pagamento gera um lançamento no fluxo de caixa
      qc.invalidateQueries({ queryKey: ["cash-transactions"] });
      qc.invalidateQueries({ queryKey: ["cash-accounts"] });
      qc.invalidateQueries({ queryKey: ["cash-flow-summary"] });
    },
  });
}

export interface UpdateRepaymentData {
  amount: number;
}

export function useUpdateRepayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRepaymentData }) =>
      api.patch<Repayment>(`/repayments/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repayments"] });
    },
  });
}
